/**
 * The `application-name` request header.
 *
 * It has always been sent as a "who is calling" label. As of AUD-9 it is also this app's CSRF
 * defence, and the server REQUIRES it on every mutating request (`CsrfHeaderFilter`): a POST, PUT,
 * PATCH or DELETE that arrives without it is answered 403.
 *
 * Why that works, in one line: the header is not CORS-safelisted, so sending it forces a preflight,
 * and the preflight is answered against the API's origin allow-list — which an attacker's page is
 * not on. The VALUE is not a secret and is not validated; there is deliberately no token to mint or
 * synchronise. The protection is the preflight.
 *
 * It lives in its own module, and is spelled once, because the app has three axios instances and the
 * header used to be re-typed in each of them — plus, at the time AUD-9 landed, in six bare
 * `axios.post` calls that were not sending it at all: session extend/invalidate and the two
 * invitation-acceptance flows, i.e. the keep-alive, logout, and every new account.
 *
 * **There are no bare `axios` mutations left.** NEW-25 routed all six through `apiClient`, whose
 * request interceptor sets this header, after breaking the `SessionService` <-> `apiClient` cycle
 * that had forced two of them (see `services/sessionStore.ts`). Only three modules now need to spell
 * the header: `apiClient`, and the two instances that deliberately bypass it so that failures are
 * not reported twice (`patientFileService`, `externalPatientApi`).
 *
 * So the rule for anything new is the short one: **go through `apiClient`.** If you have a real
 * reason to create your own axios instance — meaning you can say who reports its failures, because
 * the global dialog will not — spread {@link applicationHeader} into its defaults. Both halves are
 * enforced by the source scan in `applicationHeader.test.ts`.
 */
export const APPLICATION_NAME_HEADER = "application-name";

export const APPLICATION_NAME = "mtc-provider-app";

export const applicationHeader = {
  [APPLICATION_NAME_HEADER]: APPLICATION_NAME,
} as const;
